import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import API_BASE_URL from './config/api';
import { canAccessTab, firstAccessibleTab, hasAnyMaintenanceAccess, maintenanceTabPath, tabForMaintenancePath } from './maintenanceAccess';

export const MAINTENANCE_ACCESS_CHANGED = 'maintenanceAccessChanged';
const AccessContext = createContext(null);
// Metric drilldowns and Overview are the same server-authorized Money tab.
const policyScope = path => tabForMaintenancePath(path)==='money-overview'?'money-overview':path;
const NO_ACCESS = { status: 'loading', allowedTabs: [], hiddenTabs: [], role: '', canViewTab: () => false, refresh: () => {} };

export function useMaintenanceAccess() {
  return useContext(AccessContext) || NO_ACCESS;
}

function AccessNotice({ title, children, retry }) {
  const navigate = useNavigate();
  return (
    <main style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#F4F7FB', padding: 24 }}>
      <section style={{ maxWidth: 460, background: '#fff', padding: 32, borderRadius: 14, border: '1px solid #E6E8EC' }}>
        <h1 style={{ fontSize: 22, color: '#1f2937' }}>{title}</h1>
        <p role="status">{children}</p>
        <div style={{ display: 'flex', gap: 12 }}>
          {retry && <button type="button" onClick={retry}>Try again</button>}
          <button type="button" onClick={() => {
            localStorage.removeItem('managerToken');
            localStorage.removeItem('managerUser');
            navigate('/login', { replace: true });
          }}>Sign out</button>
        </div>
      </section>
    </main>
  );
}

export function MaintenanceAccessProvider({ children }) {
  const location = useLocation();
  const owner=localStorage.getItem('managerToken')||'';
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState({ status: 'loading', path: null, revision: -1 });
  const refresh = useCallback(() => setRevision(value => value + 1), []);
  useEffect(() => {
    const onStorage = event => {
      if (!event.key || ['managerToken', 'managerUser', MAINTENANCE_ACCESS_CHANGED].includes(event.key)) refresh();
    };
    window.addEventListener(MAINTENANCE_ACCESS_CHANGED, refresh);
    window.addEventListener('focus', refresh);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(MAINTENANCE_ACCESS_CHANGED, refresh);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('storage', onStorage);
    };
  }, [refresh]);

  useEffect(() => {
    const controller = new AbortController();
    const path = location.pathname;
    setState(previous => previous.status === 'ready' && previous.owner===owner && policyScope(previous.path)===policyScope(path)
      ? previous : { status: 'loading', path, revision });
    (async () => {
      try {
        const token = localStorage.getItem('managerToken');
        if (!token) throw new Error('No session');
        const response = await fetch(`${API_BASE_URL}/api/maintenance/access/me`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
          cache: 'no-store',
        });
        if (!response.ok) throw new Error('Access unavailable');
        const policy = await response.json();
        if (!hasAnyMaintenanceAccess(policy.role)
          || !Array.isArray(policy.hiddenTabs) || !Array.isArray(policy.allowedTabs)
          || !policy.hiddenTabs.every(key => typeof key === 'string')
          || !policy.allowedTabs.every(key => typeof key === 'string')) throw new Error('Invalid access response');
        const allowedTabs = policy.allowedTabs.filter(key => canAccessTab(policy.role, key, policy.hiddenTabs));
        if (!controller.signal.aborted) setState({ ...policy, owner:token, allowedTabs, path, revision, status: 'ready' });
      } catch (error) {
        if (!controller.signal.aborted) setState({ status: 'error', path, revision });
      }
    })();
    return () => controller.abort();
  }, [location.pathname, revision, owner]);

  // A different dashboard tab or account must verify before mounting. Within
  // the already authorized Money tab, route changes recheck in the background;
  // failed/revoked policy still removes content, and all data endpoints enforce
  // current Owner access independently. Background
  // refreshes keep the current page mounted so switching windows doesn't erase
  // unsaved form input; a failed refresh replaces it with the retry screen.
  const ready = state.status === 'ready' && state.owner===owner && policyScope(state.path)===policyScope(location.pathname);
  const value = useMemo(() => ({
    ...state, refresh,
    canViewTab: key => ready && state.allowedTabs.includes(key),
  }), [state, refresh, ready]);
  if (state.status === 'error' && state.path === location.pathname && state.revision === revision) {
    return <AccessNotice title="Couldn’t check dashboard access" retry={refresh}>Please try again to load your available pages.</AccessNotice>;
  }
  if (!ready) return <main role="status" style={{ padding: 40 }}>Loading your dashboard…</main>;
  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export function MaintenanceTabGate({ children }) {
  const location = useLocation();
  const access = useMaintenanceAccess();
  const key = tabForMaintenancePath(location.pathname);
  if (key && access.canViewTab(key)) return children;
  const first = firstAccessibleTab(access.role, access.hiddenTabs, access.allowedTabs);
  if (first) return <Navigate to={maintenanceTabPath(first)} replace />;
  return <AccessNotice title="No dashboard pages assigned">Ask an Admin to enable the pages you need.</AccessNotice>;
}
